import {
    AdvanceFilterOptionNew,
    countNonEmptyProperties,
    FillerFieldType,
    matchesFieldCondition,
    returnClearedFiltersObject,
} from '../../helper';

// ui-core's MultipleSelect takes { id, title }; the filter configs describe options as
// { value, label } like every other filter type.
const toMultipleSelectItems = (options: { value: string; label: string }[]) =>
    options.map((option) => ({ id: option.value, title: option.label }));
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
    AutoCompleteInput,
    BladeModal,
    Button,
    DateRangePicker,
    FromInputLabel,
    GroupedSwitcherPills,
    Input,
    InputFullRow,
    MultiPillSelector,
    MultipleSelect,
    Select,
    Toggle,
} from '@monavate/ui-core';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AmountRangeInputs from '../AmountRangeInputs/AmountRangeInputs';
import CardSelector from '../../../CardSelector/CardSelector';
import OrderSelector from '../../../OrderSelector/OrderSelector';

const Header = styled.div`
    padding: 32px 32px 24px;
    background: ${(props) => props.theme['Surface/secondary']}
        /* TODO: check color. Alternatives: ["Surface/secondary","Interaction/hover","Interaction/active","Interaction/selected","Interaction/focus","Interaction/disabled"] */;
`;

const MainTitle = styled.div`
    color: ${(props) => props.theme['Content/primary']}
        /* TODO: check color. Alternatives: ["Content/primary","Content/logo","Content/on-accent"] */;
    font-family: 'Poppins', serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
`;

const SecondaryTitle = styled.div`
    color: ${(props) => props.theme['Borders/active']};
    font-family: 'Poppins', serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

export const InputFilterRow = styled.div`
    padding: 12px 0 0;
    position: relative;
    label {
        margin-bottom: 0;
    }
`;

const ErrorText = styled.div`
    font-size: 14px;
    color: #eb5757 /* TODO: need to change color. There is no such color in themes.ts */;
`;

// The blade renders inside an antd Drawer, which sets a dark text colour. ui-core's
// MultipleSelect inherits its selected value text, so without this it shows black
// on the dark theme (MP-16255).
const FormContainer = styled.div`
    color: ${(props) => props.theme['Content/primary']};
`;

interface BladeModalHeaderProps {
    title: string;
    filtersCount: number;
}

const BladeModalHeader: React.FC<BladeModalHeaderProps> = ({ title, filtersCount }) => {
    return (
        <Header>
            <MainTitle>{title}</MainTitle>
            <SecondaryTitle>{`${filtersCount} filters applied`}</SecondaryTitle>
        </Header>
    );
};

const FooterContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;
const FooterRightButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    flex: 1 0 0;
`;

const adjustTransactionDate = (date: any) => {
    const timezoneOffset = -new Date().getTimezoneOffset() * 60;
    return {
        transactionDate: [date.transactionDate[0] + timezoneOffset, date.transactionDate[1]],
    };
};

const BladeModalFooter = (
    onCancelButtonClickHandler: () => void,
    onResetButtonClickHandler: () => void,
    onApplyButtonClickHandler: () => void,
    appliedFiltersCount: number,
    isResetClicked: boolean,
    isAmountValid: boolean,
    errors: Record<string, any>,
    isDirty: boolean,
) => {
    const isApplyAbble = (appliedFiltersCount < 1 && !isResetClicked) || !isAmountValid;
    const hasNoErrors = Object.keys(errors).length === 0;
    // Reset has to clear a value the user has typed but not applied — otherwise a value that
    // fails validation leaves the blade stuck: Apply is blocked by the error and Reset was
    // keyed on the applied count, which is still zero.
    const nothingToReset = appliedFiltersCount < 1 && !isDirty && hasNoErrors;
    return (
        <FooterContainer>
            <Button handleClick={onCancelButtonClickHandler} dataTestId="button-filters-cancel">
                Cancel
            </Button>
            <FooterRightButtonGroup>
                <Button
                    disabled={nothingToReset}
                    handleClick={onResetButtonClickHandler}
                    dataTestId="button-filters-reset"
                >
                    Reset
                </Button>
                <Button
                    disabled={isApplyAbble || !hasNoErrors}
                    viewType={'primary'}
                    handleClick={onApplyButtonClickHandler}
                    dataTestId="button-filters-apply"
                >
                    Apply
                </Button>
            </FooterRightButtonGroup>
        </FooterContainer>
    );
};

interface FiltersBladeModalProps {
    bladeModalHeaderDescription: string;
    isOpen: boolean;
    appliedFiltersCount: number;
    onModalCancelButtonClickHandler: () => void;
    onApplyFiltersFromModalCallback: any;
    onFilterResetCallback: any;
    appliedFilters: Record<string, any>;
    validationScheme?: any;
    options: AdvanceFilterOptionNew[];
}
const FiltersBladeModal: React.FC<FiltersBladeModalProps> = ({
    bladeModalHeaderDescription,
    isOpen,
    onModalCancelButtonClickHandler,
    onApplyFiltersFromModalCallback,
    onFilterResetCallback,
    appliedFilters,
    validationScheme,
    options,
}) => {
    const [isResetClicked, setIsResetClicked] = useState(false);
    const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);
    const [isAmountValid, setsIsAmountValid] = useState(true);

    const multiSelectOptions = options.filter((o) => o.fieldType === FillerFieldType.multiSelect);
    const refs: Record<string, any> = {};
    multiSelectOptions.forEach((o) => {
        refs[`${o.name}Ref`] = useRef<{ reset: () => void }>(null);
    });
    const handleResetMultiSelects = () => {
        const keys = Object.keys(refs);
        keys.forEach((key) => {
            refs[key].current?.reset();
        });
    };
    const {
        getValues,
        formState: { errors, isValid, isDirty },
        register,
        watch,
        setValue,
        reset,
        trigger,
    } = useForm({
        resolver: yupResolver(validationScheme),
        mode: 'all',
        defaultValues: appliedFilters,
    });
    const formValues = getValues();
    const watchedValues: Record<string, any> = {};

    options.forEach((o) => {
        watchedValues[`${o.name}Watched`] = watch(o.name);
    });

    useEffect(() => {
        const filters = getValues();
        const count = countNonEmptyProperties(filters);
        setAppliedFiltersCount(count);
    }, [formValues]);

    useEffect(() => {
        reset(appliedFilters);
    }, [appliedFilters]);
    const onDateRangeUpdateCallback = (data: any, filterType: string) => {
        setValue(filterType, data[filterType], {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const onResetButtonClickHandler = () => {
        setIsResetClicked(true);
        reset(returnClearedFiltersObject(options));
        handleResetMultiSelects();
        if (onFilterResetCallback) {
            onFilterResetCallback();
        }
    };
    const onApplyButtonClickHandler = () => {
        setIsResetClicked(false);
        const newFilters = getValues();
        onApplyFiltersFromModalCallback(newFilters);
    };

    const onCancelButtonClickHandler = () => {
        reset(appliedFilters);
        onModalCancelButtonClickHandler();
    };

    const onFilterChangeCallback = (data: any) => {
        const key = Object.keys(data)[0];
        setValue(key, data[key], {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const onAmountRangeChangeCallback = (
        fieldName: string,
        data: any,
        fieldToTriggerAfterChange?: string,
    ) => {
        setValue(fieldName, data, {
            shouldValidate: true,
            shouldDirty: true,
        });
        if (fieldToTriggerAfterChange) {
            trigger(fieldToTriggerAfterChange);
        }
    };

    const generateOptions = (options: any[]) => {
        const values = getValues();
        const newOptions = options.map((option) => {
            if (option?.restriction?.filter) {
                const value = values?.[option.restriction.filter];
                if (value && value !== option.restriction.value) {
                    return {
                        ...option,
                        disabled: true,
                    };
                }
            }
            return option;
        });
        return newOptions;
    };

    const onToggleChangeHandler = (filterName: string, afterSelectInstruction?: any) => {
        const newValue = watchedValues[`${filterName}Watched`] ? '' : 'Yes';
        setValue(filterName, newValue);
        if (newValue === 'Yes' && afterSelectInstruction) {
            if (afterSelectInstruction.type === 'setValue') {
                setValue(
                    afterSelectInstruction.filterAppliedFor,
                    afterSelectInstruction.valueApplied,
                    {
                        shouldValidate: true,
                        shouldDirty: true,
                    },
                );
            }
        }
    };

    const isOptionVisible = (option: AdvanceFilterOptionNew) =>
        !option.isHideCondition ||
        matchesFieldCondition(
            option.isHideCondition,
            watchedValues[`${option.isHideCondition.watchedField}Watched`],
        );

    const currentValues: Record<string, any> = {};
    options.forEach((o) => {
        currentValues[o.name] = watchedValues[`${o.name}Watched`];
    });

    const isOptionDisabled = (option: AdvanceFilterOptionNew) =>
        (!!option.isDisabledCondition &&
            watchedValues[`${option.isDisabledCondition.watchedField}Watched`] ===
                option.isDisabledCondition.value) ||
        !!option.isDisabled?.(currentValues);

    // A filter the gating field has hidden must stop contributing to the query and to the
    // applied count — switching Type to Bank clears whatever was picked in the card-only
    // group, so a value the user can no longer see can never reach the request.
    useEffect(() => {
        options.forEach((option) => {
            if (!option.isHideCondition || isOptionVisible(option)) {
                return;
            }
            const current = getValues()[option.name];
            const isEmpty = Array.isArray(current) ? current.length === 0 : !current;
            if (!isEmpty) {
                setValue(option.name, option.defaultValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            }
        });
    }, [JSON.stringify(watchedValues)]);

    return (
        <BladeModal
            dataTestId="modal-filters-blade"
            isOpen={isOpen}
            closeButton={true}
            handleClose={onModalCancelButtonClickHandler}
            padding={'0 32px'}
            customHeader={
                <BladeModalHeader
                    title={bladeModalHeaderDescription}
                    filtersCount={appliedFiltersCount}
                />
            }
            customBottomButton={BladeModalFooter(
                onCancelButtonClickHandler,
                onResetButtonClickHandler,
                onApplyButtonClickHandler,
                appliedFiltersCount,
                isResetClicked,
                isAmountValid,
                errors,
                isDirty,
            )}
        >
            <FormContainer>
                {options &&
                    options.map((option, index) => (
                        <div
                            key={index}
                            style={{
                                display: isOptionVisible(option) ? 'block' : 'none',
                            }}
                        >
                            {option.fieldType === FillerFieldType.input && (
                                <InputFilterRow>
                                    <FromInputLabel>{option.label}</FromInputLabel>
                                    <InputFullRow>
                                        <Input
                                            dataTestId={`input-filter-${option.name}`}
                                            name={option.name}
                                            register={() => register(option.name)}
                                            error={errors?.[option.name]?.message}
                                            addonBefore={
                                                option.searchIcon ? (
                                                    <span className="material-symbols-sharp">
                                                        search
                                                    </span>
                                                ) : undefined
                                            }
                                            disabled={isOptionDisabled(option)}
                                        />
                                    </InputFullRow>
                                </InputFilterRow>
                            )}
                            {option.fieldType === FillerFieldType.dateRangePicker && (
                                <InputFilterRow>
                                    <FromInputLabel>{option.label}</FromInputLabel>
                                    <InputFullRow>
                                        <DateRangePicker
                                            dataTestId={`date-filter-${option.name}`}
                                            filterName={option.name}
                                            onFilterUpdateCallback={(date: any) =>
                                                onDateRangeUpdateCallback(date, option.name)
                                            }
                                            isClickOutsideClearValue={true}
                                            // @ts-ignore
                                            value={formValues[option.name]}
                                        />
                                    </InputFullRow>
                                </InputFilterRow>
                            )}
                            {option.fieldType === FillerFieldType.tagSelect && option.options && (
                                <InputFilterRow>
                                    {!!option.label && (
                                        <FromInputLabel>{option.label}</FromInputLabel>
                                    )}
                                    <InputFullRow>
                                        <GroupedSwitcherPills
                                            dataTestId={`tab-filter-${option.name}`}
                                            items={option.options}
                                            onChange={(data: any) =>
                                                setValue(option.name, data, {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                })
                                            }
                                            selectedItemId={watchedValues[`${option.name}Watched`]}
                                        />
                                    </InputFullRow>
                                </InputFilterRow>
                            )}
                            {option.fieldType === FillerFieldType.tagMultiSelect &&
                                option.options && (
                                    <InputFilterRow>
                                        <FromInputLabel>{option.label}</FromInputLabel>
                                        <InputFullRow>
                                            <MultiPillSelector
                                                dataTestId={`select-filter-${option.name}`}
                                                options={generateOptions(option.options)}
                                                filterName={option.name}
                                                onFilterUpdateCallback={onFilterChangeCallback}
                                                selected={watchedValues[`${option.name}Watched`]}
                                            />
                                        </InputFullRow>
                                        {errors?.[option.name]?.message && (
                                            <ErrorText>
                                                {errors?.[option.name]?.message?.toString()}
                                            </ErrorText>
                                        )}
                                    </InputFilterRow>
                                )}
                            {option.fieldType === FillerFieldType.select && option.options && (
                                <InputFilterRow>
                                    <FromInputLabel>{option.label}</FromInputLabel>
                                    <InputFullRow>
                                        <Select
                                            dataTestId={`select-filter-${option.name}`}
                                            placeholder="Select"
                                            popupClassName="single-scroll-popup"
                                            // A filter is never mandatory, so the chosen value
                                            // has to be removable from the control itself.
                                            allowClear
                                            disabled={isOptionDisabled(option)}
                                            value={watchedValues[`${option.name}Watched`] || null}
                                            options={option.options}
                                            tooltip={option.tooltip}
                                            onChange={(value: string) => {
                                                setValue(option.name, value ?? '', {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                });
                                            }}
                                            error={errors?.[option.name]?.message}
                                        />
                                    </InputFullRow>
                                </InputFilterRow>
                            )}
                            {option.fieldType === FillerFieldType.multiSelect && option.options && (
                                <InputFilterRow>
                                    <FromInputLabel>{option.label}</FromInputLabel>
                                    <InputFullRow>
                                        <MultipleSelect
                                            dataTestId={`select-filter-${option.name}`}
                                            ref={refs[`${option.name}Ref`]}
                                            items={toMultipleSelectItems(option.options)}
                                            // Controlled, so Reset, Cancel and a hidden-field
                                            // clear all show up in the control straight away.
                                            value={watchedValues[`${option.name}Watched`] || []}
                                            placeholder="Select"
                                            disabled={isOptionDisabled(option)}
                                            onChange={(items: any) => {
                                                setValue(option.name, items, {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                });
                                                option.onChange && option.onChange(items);
                                            }}
                                            tooltip={option.tooltip}
                                        />
                                    </InputFullRow>
                                    {errors?.[option.name]?.message && (
                                        <ErrorText>
                                            {errors?.[option.name]?.message?.toString()}
                                        </ErrorText>
                                    )}
                                </InputFilterRow>
                            )}
                            {option.fieldType === FillerFieldType.autocomplete &&
                                option.options && (
                                    <InputFilterRow>
                                        <FromInputLabel>{option.label}</FromInputLabel>
                                        <InputFullRow>
                                            <AutoCompleteInput
                                                dataTestId={`input-filter-${option.name}`}
                                                register={() => register(option.name)}
                                                value={watchedValues[`${option.name}Watched`]}
                                                onChange={(value: string) => {
                                                    setValue(option.name, value);
                                                    option.onChange && option.onChange(value);
                                                }}
                                                options={option?.options || []}
                                            />
                                        </InputFullRow>
                                    </InputFilterRow>
                                )}
                            {option.fieldType === FillerFieldType.toggle && (
                                <InputFilterRow>
                                    <InputFullRow>
                                        <Toggle
                                            dataTestId={`toggle-filter-${option.name}`}
                                            toggleText={option.label}
                                            disabled={isOptionDisabled(option)}
                                            checked={!!watchedValues[`${option.name}Watched`]}
                                            handleChange={() =>
                                                onToggleChangeHandler(
                                                    option.name,
                                                    option.afterSelectInstruction,
                                                )
                                            }
                                        />
                                    </InputFullRow>
                                </InputFilterRow>
                            )}
                            {option.fieldType === FillerFieldType.amountRange && (
                                <InputFilterRow>
                                    <FromInputLabel>{option.label}</FromInputLabel>
                                    <InputFullRow>
                                        <AmountRangeInputs
                                            onChangeCallback={(value: any) =>
                                                onAmountRangeChangeCallback(
                                                    option.name,
                                                    value,
                                                    option.fieldToTriggerAfterChange,
                                                )
                                            }
                                            defaultValues={watchedValues[`${option.name}Watched`]}
                                            setsIsAmountValid={setsIsAmountValid}
                                        />
                                    </InputFullRow>
                                </InputFilterRow>
                            )}
                            {option.fieldType === FillerFieldType.cardSelector && (
                                <InputFilterRow>
                                    <FromInputLabel>{option.label}</FromInputLabel>
                                    <InputFullRow>
                                        <CardSelector
                                            dataTestId={`select-filter-${option.name}`}
                                            disabled={isOptionDisabled(option)}
                                            onChangeCallback={(value: any) =>
                                                setValue(option.name, value, {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                })
                                            }
                                            defaultValue={watchedValues[`${option.name}Watched`]}
                                            options={option.options}
                                        />
                                    </InputFullRow>
                                </InputFilterRow>
                            )}
                            {option.fieldType === FillerFieldType.orderSelector && (
                                <InputFilterRow>
                                    <FromInputLabel>{option.label}</FromInputLabel>
                                    <InputFullRow>
                                        <OrderSelector
                                            onChangeCallback={(value: any) =>
                                                setValue(option.name, value, {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                })
                                            }
                                            defaultValue={watchedValues[`${option.name}Watched`]}
                                            options={option.options}
                                        />
                                    </InputFullRow>
                                </InputFilterRow>
                            )}
                        </div>
                    ))}
            </FormContainer>
        </BladeModal>
    );
};

export default FiltersBladeModal;
